import React, { Component, ComponentType } from 'react';
import { QueryResult } from 'react-apollo';
import { FetchMoreOptions } from 'apollo-client';

import {
  GetInboxItemsComponent,
  GetInboxItemsDocument,
  Inbox,
  InboxItemCreatedDocument,
} from 'api';
import { LoaderTypes } from 'types/app';
import { QueryManager } from 'components';
import { withUser, IWithUser } from '../UserContext';

interface IProps {
  loader: LoaderTypes;
  limit: number;
}

export interface IWithInboxItems {
  inbox: Inbox[];
  subscribeToInbox: () => void;
  onLoadMore: () => void;
  exhausted: boolean;
  loading: boolean;
}

type WrappedComponent<T> = ComponentType<T & IWithInboxItems>;

const withInboxItems = <T extends {}>(options: IProps) => (ChildComponent: WrappedComponent<T>) => {
  const { loader, limit } = options;

  class WithInboxItems extends Component<T & IWithUser> {
    updateQuery: FetchMoreOptions['updateQuery'] = (prev, { fetchMoreResult }) => {
      if (!fetchMoreResult) {
        return prev;
      }

      const prevResult = prev.getInboxItems;
      const nextResult = fetchMoreResult.getInboxItems;
      const updatedResult = {
        ...prevResult,
        ...nextResult,
        inbox: [...prevResult.inbox, ...nextResult.inbox],
      };

      return {
        getInboxItems: updatedResult,
      };
    };

    handleLoadMore = (queryResult: QueryResult) => {
      const { loading, error, data, fetchMore } = queryResult;
      const { created_at, id, paginate } = data.getInboxItems;

      if (loading || error || !paginate) {
        return Promise.resolve();
      }

      return fetchMore({
        query: GetInboxItemsDocument,
        variables: { created_at, id, limit },
        updateQuery: this.updateQuery,
      });
    };

    subscribeToInbox = (queryResult: QueryResult) => {
      const { user } = this.props;

      queryResult.subscribeToMore({
        document: InboxItemCreatedDocument,
        variables: { user_id: user.id },
        updateQuery: (prev, { subscriptionData }) => {
          if (!subscriptionData.data) {
            return prev;
          }

          const { inboxItemCreated } = subscriptionData.data;

          return {
            ...prev,
            getInboxItems: {
              ...prev.getInboxItems,
              inbox: [inboxItemCreated, ...prev.getInboxItems.inbox],
            },
          };
        },
      });
    };

    render() {
      return (
        <GetInboxItemsComponent notifyOnNetworkStatusChange variables={{ limit }}>
          {queryResult => (
            <QueryManager loader={loader} {...queryResult}>
              {({ data, loading }) => {
                if (!data || !data.getInboxItems) {
                  return null;
                }

                const { getInboxItems } = data;
                const { inbox, paginate } = getInboxItems;

                const props = {
                  loading,
                  inbox,
                  exhausted: !paginate,
                  onLoadMore: () => this.handleLoadMore(queryResult),
                  subscribeToInbox: () => this.subscribeToInbox(queryResult),
                };

                return <ChildComponent {...(this.props as T)} {...props} />;
              }}
            </QueryManager>
          )}
        </GetInboxItemsComponent>
      );
    }
  }

  return withUser(WithInboxItems);
};

export default withInboxItems;
